import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import axios from 'axios';
import { Worker, Viewer } from '@react-pdf-viewer/core';
import '@react-pdf-viewer/core/lib/styles/index.css';
import { addToCart } from '../../store/cartSlice';
import { addToWishlist, removeFromWishlist } from '../../store/wishlistSlice';

import ShopNav from '../../components/shopNav/ShopNav';
import NavCategorias from '../../components/navbar/NavCategorias';
import Footer from '../../components/footer/footer';
import ServiceHighlights from '../../components/servicehighlights/ServiceHighlights';
import ProductImageGallery from '../../components/shop/ProductImageGallery';
import { showShopToast } from '../../utils/shopToast';
import { categories } from '../../config/categories';

import './ProductDetailPage.css';

// ✅ Variables de entorno
const API_URL = import.meta.env.VITE_API_URL;

// ✅ Formato moneda COP
const formatCOP = (value) =>
  new Intl.NumberFormat('es-CO', {
    style: 'currency',
    currency: 'COP',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value);

const ProductDetailPage = () => {
  const { productId } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const wishlistItems = useSelector((state) => state.wishlist.items || []);

  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [quantity, setQuantity] = useState(1);
  const [video, setVideo] = useState(null);
  // checking | ready | empty | searching | notfound | error
  const [videoStatus, setVideoStatus] = useState('checking');

  // ✅ Fetch producto
  useEffect(() => {
    const fetchProductDetail = async () => {
      try {
        setLoading(true);
        setError(null);
        setQuantity(1);

        const response = await axios.get(`${API_URL}/stock/producto/${productId}`);
        const data = response.data;
        setProduct(data);

        if (data.pdfUrl) {
          try {
            const parsedPdf = JSON.parse(data.pdfUrl);
            const originalPdfUrl = parsedPdf.pdf1;
            setPdfUrl(
              `${API_URL}/stock/proxy-pdf?url=${encodeURIComponent(originalPdfUrl)}`
            );
          } catch {
            console.warn('⚠ Error parseando pdfUrl');
          }
        }
      } catch (err) {
        setError('Error al cargar los detalles del producto.');
        console.error('Error al cargar producto:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchProductDetail();
  }, [productId]);

  // ✅ Toggle modal PDF
  const toggleModal = useCallback(() => setShowModal((prev) => !prev), []);

  // ✅ Añadir al carrito
  const handleAddToCart = useCallback(() => {
    if (product) {
      for (let i = 0; i < quantity; i += 1) dispatch(addToCart(product));
      showShopToast(
        quantity > 1 ? `${quantity} unidades agregadas al carrito` : 'Producto agregado al carrito',
        'success'
      );
    }
  }, [dispatch, product, quantity]);

  // ✅ Wishlist
  const isInWishlist = useCallback(
    (id) => wishlistItems.some((item) => item.id === id),
    [wishlistItems]
  );

  const handleToggleWishlist = useCallback(() => {
    if (!product) return;
    if (isInWishlist(product.id)) {
      dispatch(removeFromWishlist(product.id));
      showShopToast('Producto eliminado de la lista de deseos', 'warning');
    } else {
      dispatch(addToWishlist(product));
      showShopToast('Producto agregado a la lista de deseos', 'success');
    }
  }, [dispatch, product, isInWishlist]);

  // ✅ Navegar a marca
  const handleBrandClick = useCallback(
    (brand) => {
      if (!brand) return;
      navigate(`/marcas/${brand.toLowerCase().replace(/\s+/g, '-')}`);
    },
    [navigate]
  );

  // ✅ Al cargar el producto, muestra el video solo si ya esta guardado en la base de datos
  useEffect(() => {
    if (!product?.id) return undefined;
    const controller = new AbortController();
    setVideo(null);
    setVideoStatus('checking');

    axios
      .get(`${API_URL}/product-videos/${product.id}`, {
        params: { cached: 1 },
        signal: controller.signal
      })
      .then(({ data }) => {
        if (data?.video) {
          setVideo(data.video);
          setVideoStatus('ready');
        } else {
          setVideoStatus('empty');
        }
      })
      .catch((err) => {
        if (axios.isCancel(err)) return;
        console.error('Error al consultar video:', err);
        setVideoStatus('empty');
      });

    return () => controller.abort();
  }, [product?.id]);

  // ✅ Sin video guardado: el backend lo busca con Gemini + YouTube y lo guarda
  const loadVideo = async () => {
    if (!product) return;
    setVideoStatus('searching');

    try {
      const { data } = await axios.get(`${API_URL}/product-videos/${product.id}`);
      if (data?.video) {
        setVideo(data.video);
        setVideoStatus('ready');
      } else {
        setVideoStatus('notfound');
      }
    } catch (err) {
      console.error('Error al obtener video:', err);
      setVideoStatus('error');
    }
  };

  const showContent = !loading && !error && !!product;

  const categoryLabel =
    categories.find((cat) => cat.id === product?.category)?.name || product?.category;

  const stockInfo = (() => {
    const stock = Number(product?.stock);
    if (!Number.isFinite(stock)) return null;
    if (stock <= 0) return { className: 'is-order', icon: 'bi-clock-history', label: 'Bajo pedido' };
    if (stock <= 3) return { className: 'is-low', icon: 'bi-exclamation-circle', label: `Últimas ${stock} unidades` };
    return { className: 'is-ok', icon: 'bi-check-circle-fill', label: 'Disponible' };
  })();

  const quoteUrl = product
    ? `https://wa.me/573012201583?text=${encodeURIComponent(
        `Hola Vintage Pro Audio, quiero cotizar: ${product.name}${product.brand?.name ? ` (${product.brand.name})` : ''}. ${window.location.href}`
      )}`
    : '#';

  return (
    <div className="product-page">
      <ShopNav />
      <NavCategorias />

      <main className="product-page-main">
        <section className="product-page-hero product-page-hero--compact product-page-elevated-wrap product-page-elevated-wrap--hero">
          <div className="container product-page-hero-inner">
            <div className="product-page-hero-copy">
              {showContent && (
                <nav aria-label="breadcrumb" className="product-breadcrumb">
                  <Link to="/">Inicio</Link>
                  {product.category && (
                    <>
                      <span aria-hidden="true">›</span>
                      <Link to={`/categorias/${product.category}`}>{categoryLabel}</Link>
                    </>
                  )}
                  <span aria-hidden="true">›</span>
                  <span className="product-breadcrumb-current">{product.name}</span>
                </nav>
              )}
              <h1 className="display-5">Detalle de producto</h1>
              <p className="lead">
                Revisa fotos, precio, marca y ficha tecnica en una vista enfocada para decidir tu compra.
              </p>
            </div>
            <div className="product-page-hero-logo-wrap" aria-hidden="true">
              <img src="/images/VPA.png" alt="Vintage Pro Audio" className="product-page-hero-logo" />
            </div>
          </div>
        </section>

        <section className="container product-page-results">
          {loading ? (
            <div className="product-page-state text-center">Cargando producto...</div>
          ) : error ? (
            <div className="alert alert-danger text-center">{error}</div>
          ) : !product ? (
            <div className="alert alert-warning text-center">Producto no encontrado</div>
          ) : (
            <div className="row g-4 align-items-stretch product-detail-grid">
              <div className="col-lg-6">
                <article className="card border-0 product-gallery-card h-100">
                  <div className="card-body product-card-body">
                    <div className="custom-gallery-wrapper product-gallery-wrapper">
                      <ProductImageGallery
                        imageUrl={product.imageUrl}
                        productName={product.name}
                        showPlayButton={false}
                        showThumbnails={!isFullscreen}
                        onScreenChange={setIsFullscreen}
                      />
                    </div>
                  </div>
                </article>
              </div>

              <div className="col-lg-6">
                <article className="card border-0 product-info-card h-100">
                  <div className="card-body product-card-body d-flex flex-column">
                    <div className="product-title-row">
                      <h2 className="product-name mb-0">{product.name}</h2>
                      <button
                        type="button"
                        className="product-wishlist-btn"
                        title={isInWishlist(product.id) ? 'Eliminar de la lista de deseos' : 'Agregar a la lista de deseos'}
                        onClick={handleToggleWishlist}
                      >
                        <i className={`bi bi-heart${isInWishlist(product.id) ? '-fill text-danger' : ''}`}></i>
                      </button>
                    </div>

                    {product.brand && (
                      <div
                        className="product-brand-box"
                        onClick={() => handleBrandClick(product.brand?.name || '')}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            handleBrandClick(product.brand?.name || '');
                          }
                        }}
                        role="button"
                        tabIndex={0}
                      >
                        {product.brand?.logoUrl && (
                          <img
                            src={product.brand.logoUrl}
                            alt={product.brand?.name || 'Marca'}
                            className="product-brand-logo"
                          />
                        )}
                        <span className="brand-link">{product.brand?.name || ''}</span>
                      </div>
                    )}

                    <div className="product-price-box">
                      <span className="product-price-label">Precio en COP</span>
                      <h3 className="product-price-value mb-0">{formatCOP(product.priceCOP)}</h3>
                      {stockInfo && (
                        <span className={`product-stock-badge ${stockInfo.className}`}>
                          <i className={`bi ${stockInfo.icon} me-1`}></i>
                          {stockInfo.label}
                        </span>
                      )}
                    </div>

                    <p className="product-short-description">{product.description}</p>

                    <div className="product-primary-actions mt-auto">
                      <div className="product-qty" role="group" aria-label="Cantidad">
                        <button
                          type="button"
                          aria-label="Disminuir cantidad"
                          onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                          disabled={quantity <= 1}
                        >
                          −
                        </button>
                        <span aria-live="polite">{quantity}</span>
                        <button
                          type="button"
                          aria-label="Aumentar cantidad"
                          onClick={() => setQuantity((q) => Math.min(10, q + 1))}
                          disabled={quantity >= 10}
                        >
                          +
                        </button>
                      </div>
                      <button
                        className="btn btn-lg add-to-cart-btn"
                        onClick={handleAddToCart}
                      >
                        Agregar al carrito
                      </button>
                    </div>

                    <a
                      className="product-quote-btn"
                      href={quoteUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <i className="bi bi-whatsapp me-2"></i>Cotizar por WhatsApp
                    </a>

                    {pdfUrl && (
                      <div className="pdf-section mt-3">
                        <h4 className="mb-3">Ficha tecnica (PDF)</h4>
                        <button className="btn product-pdf-btn" onClick={toggleModal}>
                          Ver en pantalla completa
                        </button>
                      </div>
                    )}
                  </div>
                </article>
              </div>
            </div>
          )}
        </section>

        {showContent && (
          <section className="container product-page-results product-ai-wrap">
            <div className="product-video-card">
              <header className="product-video-header">
                <span className="product-video-icon" aria-hidden="true">
                  <i className="bi bi-play-btn-fill"></i>
                </span>
                <div>
                  <h5 className="product-video-title">Video del producto</h5>
                  <p className="product-video-subtitle">Reseña o demo sugerida por IA</p>
                </div>
                <span className="product-video-badge">
                  <i className="bi bi-stars me-1"></i>IA
                </span>
              </header>

              {videoStatus === 'ready' && video ? (
                <div className="product-video-body">
                  <div className="product-video-frame ratio ratio-16x9">
                    <iframe
                      src={video.embedUrl}
                      title={video.title || `Video de ${product.name}`}
                      allow="accelerometer; encrypted-media; gyroscope; picture-in-picture; fullscreen"
                      allowFullScreen
                      loading="lazy"
                      referrerPolicy="strict-origin-when-cross-origin"
                    ></iframe>
                  </div>
                  <p className="product-video-caption">
                    <i className="bi bi-youtube me-2"></i>
                    <span>{video.title}</span>
                    {video.channelTitle && (
                      <span className="product-video-channel"> · {video.channelTitle}</span>
                    )}
                  </p>
                </div>
              ) : videoStatus === 'checking' ? (
                <div className="product-video-body">
                  <div className="product-video-skeleton ratio ratio-16x9" aria-busy="true" />
                </div>
              ) : (
                <div className="product-video-empty">
                  {videoStatus === 'searching' ? (
                    <div className="d-flex justify-content-center align-items-center gap-2">
                      <div className="spinner-border spinner-border-sm text-info" role="status" />
                      <span>Buscando el mejor video en YouTube...</span>
                    </div>
                  ) : (
                    <p className="product-ai-helper-text">
                      {videoStatus === 'notfound'
                        ? 'No encontramos un video para este producto por ahora.'
                        : videoStatus === 'error'
                          ? 'Ocurrió un error al buscar el video. Intenta de nuevo.'
                          : 'Aún no hay video de este producto. Deja que Gemini lo busque en YouTube.'}
                    </p>
                  )}

                  <button
                    className="btn product-ai-btn mt-2"
                    onClick={loadVideo}
                    disabled={videoStatus === 'searching'}
                  >
                    <i className="bi bi-search me-2"></i>
                    {videoStatus === 'searching' ? 'Buscando...' : 'Buscar video'}
                  </button>
                </div>
              )}
            </div>
          </section>
        )}

        <section className="product-page-elevated-wrap">
          <ServiceHighlights />
        </section>

        <section className="container product-page-results product-warning-wrap">
          <div className="product-warning-banner" role="alert">
            No encuentras lo que buscas? Chatea con nosotros y con gusto lo cotizamos, incluso si no lo ves en el catalogo.
          </div>
        </section>
      </main>

      {showModal && (
        <div className="product-pdf-backdrop" aria-modal="true" role="dialog" onClick={toggleModal}>
          <div className="modal-dialog modal-dialog-centered modal-lg product-pdf-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-content product-pdf-content">
              <div className="modal-header product-pdf-header">
                <h5 className="modal-title">Documento PDF</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={toggleModal}
                ></button>
              </div>
              <div className="modal-body product-pdf-body">
                <Worker workerUrl="https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js">
                  <div className="product-pdf-viewer-wrap">
                    <Viewer fileUrl={pdfUrl} />
                  </div>
                </Worker>
              </div>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default ProductDetailPage;
